import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Input, Modal, Tag } from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  CloseCircleFilled,
  ExclamationCircleFilled,
  KeyOutlined,
  SoundFilled,
  SoundOutlined,
  SwapOutlined
} from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import QrCamera from './QrCamera';
import {
  playSuccessFeedback,
  playErrorFeedback,
  playDuplicateFeedback
} from './feedback';
import styles from './QrScan.module.less';

const TOAST_AUTO_HIDE_MS = 1800;
const TOAST_ERROR_AUTO_HIDE_MS = 2600;

// Many QRs are URLs like https://app/.../D-001. The label is the last path
// segment. If it isn't a URL, just trim the raw value.
const extractLabel = (decoded) => {
  try {
    const url = new URL(decoded);
    const segs = url.pathname.split('/').filter(Boolean);
    return segs[segs.length - 1] || decoded.trim();
  } catch {
    return decoded.trim();
  }
};

/**
 * Gold-standard scan-against-target-list modal.
 *
 * Props:
 *   visible           bool
 *   onCancel          () => void
 *   onConfirm         (labels: string[]) => void  — confirmed subset of targetLabels
 *   targetLabels      string[]
 *   title             string
 *   confirmButtonText string         (default "Confirm")
 *   loading           bool           — confirm button spinner
 *   noun              { singular, plural } — e.g. { singular: 'drum', plural: 'drums' }
 *   manualPlaceholder string         — e.g. "Type drum label manually (e.g. D-123)"
 */
const QrScanChecklistModal = ({
  visible,
  onCancel,
  onConfirm,
  targetLabels = [],
  title = 'Scan',
  confirmButtonText = 'Confirm',
  loading = false,
  noun = { singular: 'item', plural: 'items' },
  manualPlaceholder = 'Type label manually',
  // Opt-in extras (defaults preserve existing behavior for other consumers):
  initialConfirmed = [], // labels to pre-mark as confirmed when opening
  onItemConfirmed, // (label) => void — fired on each new confirmation (live save)
  alwaysShowChecklist = false, // checklist always expanded, no collapse toggle
  hideManualEntry = false // remove the "Type manually" section
}) => {
  const [confirmed, setConfirmed] = useState(() => new Set(initialConfirmed));
  const [facingMode, setFacingMode] = useState('environment');
  const [muted, setMuted] = useState(false);
  const [toast, setToast] = useState(null); // { kind, label }
  const [flash, setFlash] = useState(null); // 'success'|'error'|'duplicate'
  const [statusLine, setStatusLine] = useState(null); // last-scan summary
  const [showChecklist, setShowChecklist] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [partialVisible, setPartialVisible] = useState(false);
  const [cameraOk, setCameraOk] = useState(true);

  const targetLabelsRef = useRef(targetLabels);
  targetLabelsRef.current = targetLabels;
  const confirmedRef = useRef(confirmed);
  confirmedRef.current = confirmed;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const toastTimerRef = useRef(null);
  const flashTimerRef = useRef(null);
  const initialConfirmedRef = useRef(initialConfirmed);
  initialConfirmedRef.current = initialConfirmed;
  const onItemConfirmedRef = useRef(onItemConfirmed);
  onItemConfirmedRef.current = onItemConfirmed;

  // Seed already-confirmed labels on open; reset everything on close.
  useEffect(() => {
    if (visible) {
      setConfirmed(new Set(initialConfirmedRef.current));
      return;
    }
    setConfirmed(new Set());
    setToast(null);
    setFlash(null);
    setStatusLine(null);
    setShowChecklist(false);
    setShowManual(false);
    setManualInput('');
    setPartialVisible(false);
    setCameraOk(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, [visible]);

  // Tidy timers on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  const showFeedback = useCallback((kind, label) => {
    setFlash(kind);
    setToast({ kind, label });
    if (kind === 'success') playSuccessFeedback(mutedRef.current);
    else if (kind === 'error') playErrorFeedback(mutedRef.current);
    else playDuplicateFeedback(mutedRef.current);

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), 500);

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(
      () => setToast(null),
      kind === 'error' ? TOAST_ERROR_AUTO_HIDE_MS : TOAST_AUTO_HIDE_MS
    );
  }, []);

  const processLabel = useCallback(
    (rawLabel) => {
      const upper = String(rawLabel || '')
        .toUpperCase()
        .trim();
      if (!upper) return;

      const matched = targetLabelsRef.current.find(
        (l) => String(l).toUpperCase() === upper
      );

      if (!matched) {
        setStatusLine({ kind: 'error', label: upper });
        showFeedback('error', upper);
        return;
      }

      const alreadyDone = confirmedRef.current.has(matched);
      if (alreadyDone) {
        setStatusLine({ kind: 'duplicate', label: matched });
        showFeedback('duplicate', matched);
        return;
      }

      setConfirmed((prev) => {
        const next = new Set(prev);
        next.add(matched);
        return next;
      });
      // Live persistence hook — fired once per new confirmation.
      if (onItemConfirmedRef.current) onItemConfirmedRef.current(matched);
      setStatusLine({ kind: 'success', label: matched });
      showFeedback('success', matched);
    },
    [showFeedback]
  );

  const handleCameraDecode = useCallback(
    (decoded) => {
      processLabel(extractLabel(decoded));
    },
    [processLabel]
  );

  const handleCameraError = useCallback((msg) => {
    setCameraOk(false);
    setShowManual(true); // auto-expand manual entry when camera fails
    setStatusLine({ kind: 'error', label: msg });
  }, []);

  const handleManualSubmit = () => {
    const val = manualInput.trim();
    if (!val) return;
    processLabel(val);
    setManualInput('');
  };

  const handleConfirmClick = () => {
    if (confirmed.size < targetLabels.length) {
      setPartialVisible(true);
    } else {
      onConfirm(Array.from(confirmed));
    }
  };

  const switchCamera = () =>
    setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'));

  const confirmedList = useMemo(
    () => targetLabels.filter((l) => confirmed.has(l)),
    [targetLabels, confirmed]
  );
  const pendingList = useMemo(
    () => targetLabels.filter((l) => !confirmed.has(l)),
    [targetLabels, confirmed]
  );

  const counterText = `${confirmed.size} / ${targetLabels.length}`;

  return (
    <>
      <CustomModal
        title={title}
        visible={visible && !partialVisible}
        onCancel={onCancel}
        customFooter={false}
        footer={null}
        width={560}
        destroyOnClose
      >
        {/* Camera + overlays */}
        <div className={styles.scanner_shell}>
          <QrCamera
            active={visible && !partialVisible}
            facingMode={facingMode}
            onDecode={handleCameraDecode}
            onCameraError={handleCameraError}
          />

          {/* Top overlay: switch + counter + mute */}
          <div className={styles.overlay_top}>
            <button
              type="button"
              className={styles.overlay_icon_btn}
              onClick={switchCamera}
              disabled={!cameraOk}
              aria-label="Switch camera"
              title="Switch camera"
            >
              <SwapOutlined />
            </button>
            <span className={styles.overlay_counter}>{counterText}</span>
            <button
              type="button"
              className={styles.overlay_icon_btn}
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? 'Unmute scan feedback' : 'Mute scan feedback'}
              title={muted ? 'Sound off' : 'Sound on'}
            >
              {muted ? <SoundOutlined /> : <SoundFilled />}
            </button>
          </div>

          {/* Flash overlay */}
          {flash && (
            <div
              key={`flash-${Date.now()}`}
              className={`${styles.flash} ${
                flash === 'success'
                  ? styles.flash_success
                  : flash === 'error'
                  ? styles.flash_error
                  : styles.flash_duplicate
              }`}
            />
          )}

          {/* Toast bar over bottom of camera */}
          {toast && (
            <div
              className={`${styles.toast} ${
                toast.kind === 'success'
                  ? styles.toast_success
                  : toast.kind === 'error'
                  ? styles.toast_error
                  : styles.toast_duplicate
              }`}
            >
              {toast.kind === 'success' && <CheckCircleFilled />}
              {toast.kind === 'error' && <CloseCircleFilled />}
              {toast.kind === 'duplicate' && <ExclamationCircleFilled />}
              <span>
                {toast.kind === 'success' && `${toast.label} confirmed`}
                {toast.kind === 'error' && `${toast.label} not in selection`}
                {toast.kind === 'duplicate' && `${toast.label} already scanned`}
              </span>
            </div>
          )}
        </div>

        {/* Last-scan status line (under camera) */}
        <div className={styles.section_status}>
          {statusLine ? (
            <>
              <span className={styles.section_status_label}>Last:</span>
              <span style={{ fontFamily: 'monospace' }}>
                {statusLine.label}
              </span>
              {statusLine.kind === 'success' && (
                <Tag color="success" style={{ marginLeft: 'auto' }}>
                  Confirmed
                </Tag>
              )}
              {statusLine.kind === 'duplicate' && (
                <Tag color="warning" style={{ marginLeft: 'auto' }}>
                  Already scanned
                </Tag>
              )}
              {statusLine.kind === 'error' && (
                <Tag color="error" style={{ marginLeft: 'auto' }}>
                  Not in selection
                </Tag>
              )}
            </>
          ) : (
            <span style={{ color: '#8c8c8c' }}>
              Point the camera at a QR code to begin.
            </span>
          )}
        </div>

        {/* Full checklist — always expanded when alwaysShowChecklist, else collapsible */}
        {alwaysShowChecklist ? (
          <div className={styles.section_status}>
            <span className={styles.section_status_label}>
              Containers ({confirmed.size} of {targetLabels.length})
            </span>
            <span
              style={{ marginLeft: 'auto', fontSize: 12, color: '#8c8c8c' }}
            >
              {pendingList.length} remaining
            </span>
          </div>
        ) : (
          <button
            type="button"
            className={styles.collapsible_toggle}
            onClick={() => setShowChecklist((v) => !v)}
          >
            <span className={styles.collapsible_toggle_label}>
              {showChecklist ? <CaretDownOutlined /> : <CaretRightOutlined />}
              View all ({confirmed.size} of {targetLabels.length})
            </span>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
              {pendingList.length} remaining
            </span>
          </button>
        )}
        {(alwaysShowChecklist || showChecklist) && (
          <div className={styles.collapsible_body}>
            <div className={styles.checklist_grid}>
              {targetLabels.map((l) =>
                confirmed.has(l) ? (
                  <div
                    key={l}
                    className={`${styles.checklist_item} ${styles.checklist_item_confirmed}`}
                  >
                    <CheckCircleFilled /> {l}
                  </div>
                ) : (
                  <div
                    key={l}
                    className={`${styles.checklist_item} ${styles.checklist_item_pending}`}
                  >
                    <ClockCircleOutlined /> {l}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Collapsible: manual entry */}
        {!hideManualEntry && (
          <>
            <button
              type="button"
              className={styles.collapsible_toggle}
              onClick={() => setShowManual((v) => !v)}
            >
              <span className={styles.collapsible_toggle_label}>
                {showManual ? <CaretDownOutlined /> : <CaretRightOutlined />}
                <KeyOutlined /> Type manually
              </span>
            </button>
            {showManual && (
              <div className={styles.collapsible_body}>
                <div className={styles.manual_entry_row}>
                  <Input
                    placeholder={manualPlaceholder}
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    onPressEnter={handleManualSubmit}
                    allowClear
                    autoFocus
                  />
                  <Button
                    onClick={handleManualSubmit}
                    disabled={!manualInput.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            className={styles.footer_button}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className={styles.footer_button}
            type="primary"
            onClick={handleConfirmClick}
            loading={loading}
            disabled={confirmed.size === 0 || loading}
          >
            {confirmed.size === targetLabels.length
              ? confirmButtonText
              : `${confirmButtonText} (${confirmed.size} scanned)`}
          </Button>
        </div>
      </CustomModal>

      {/* Partial-confirm sub-dialog */}
      <Modal
        title="Proceed with partial selection?"
        visible={partialVisible}
        onCancel={() => setPartialVisible(false)}
        footer={[
          <Button key="back" onClick={() => setPartialVisible(false)}>
            Scan Remaining
          </Button>,
          <Button
            key="proceed"
            type="primary"
            loading={loading}
            onClick={() => {
              setPartialVisible(false);
              onConfirm(Array.from(confirmed));
            }}
          >
            Proceed with {confirmed.size}{' '}
            {confirmed.size === 1 ? noun.singular : noun.plural}
          </Button>
        ]}
        width={480}
      >
        <p style={{ marginBottom: 12 }}>
          Only <strong>{confirmed.size}</strong> of{' '}
          <strong>{targetLabels.length}</strong> {noun.plural} have been
          scanned. Unscanned {noun.plural} will <strong>not</strong> be affected
          by this operation.
        </p>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              marginBottom: 4,
              fontSize: 12,
              color: '#595959',
              fontWeight: 600
            }}
          >
            CONFIRMED ({confirmedList.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {confirmedList.map((l) => (
              <Tag key={l} color="success" style={{ fontFamily: 'monospace' }}>
                {l}
              </Tag>
            ))}
          </div>
        </div>
        <div>
          <div
            style={{
              marginBottom: 4,
              fontSize: 12,
              color: '#595959',
              fontWeight: 600
            }}
          >
            NOT SCANNED ({pendingList.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {pendingList.map((l) => (
              <Tag key={l} color="warning" style={{ fontFamily: 'monospace' }}>
                {l}
              </Tag>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default QrScanChecklistModal;
